import React, { useState, useEffect } from 'react';
import { FormControl, InputLabel, Select, MenuItem, CircularProgress, Typography, Chip, Box, Divider } from '@mui/material';
import { organizationApi } from '../../../services/api';
import { useAlert } from '../../../contexts/AlertContext';
import { COMPANY_RETURN_TYPES } from '../../../constants/selectorReturnTypes';
import { getCompanyTypeColor, getCompanyTypeTextColor } from '../../../constants/companyTypes';

// Legacy export for backward compatibility
export const RETURN_VALUE_TYPES = COMPANY_RETURN_TYPES;

const CompanySelector = ({ 
  value, 
  onChange, 
  label = "Contract Company",
  required = false,
  disabled = false,
  fullWidth = true,
  returnValue = COMPANY_RETURN_TYPES.ID, // COMPANY_RETURN_TYPES.ID or COMPANY_RETURN_TYPES.NAME
  clusterId = null, // Filter companies by cluster ID
  clusterName = null, // Filter companies by cluster name (alternative to clusterId)
  // ADR-142 §8 — the AJAR mode: show every company, but WARN when the
  // selected one belongs to a different cluster than the person's. Unlike
  // clusterId (which filters), this leaves cross-cluster contracts
  // representable — employee.cluster != company.cluster is legitimate.
  warnClusterId = null,
  showCountry = false, // Show country name in a chip next to company name
  includeGlobalCompanies = false, // When filtering by cluster, also include companies without a cluster
  // Remove open, onOpen, onClose props - let Material-UI handle dropdown state
  ...props 
}) => {
  const [companies, setCompanies] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const { showAlert } = useAlert();

  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        setIsLoading(true);
        const companiesData = await organizationApi.getCompanies();
        setCompanies(companiesData);
      } catch (error) {
        showAlert('Failed to load companies', 'error', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchCompanies();
  }, [showAlert]);

  const handleChange = (event) => {
    if (onChange) {
      const selectedCompanyId = event.target.value;
      
      if (returnValue === COMPANY_RETURN_TYPES.NAME) {
        // Find the company object and return its name
        const selectedCompany = companies.find(company => company.id === selectedCompanyId);
        onChange(selectedCompany?.name || '');
      } else {
        // Return the ID (default behavior)
        onChange(selectedCompanyId);
      }
    }
  };

  // Filter companies by cluster if clusterId or clusterName is provided
  const filteredCompanies = companies.filter(company => {
    // If no cluster filter is specified, show all companies
    if (!clusterId && !clusterName) return true;

    // Check if this is a global company (no cluster assigned)
    const isGlobalCompany = !company.clusterId;

    // Include global companies if the flag is set
    if (includeGlobalCompanies && isGlobalCompany) {
      return true;
    }

    // Filter by cluster ID if provided
    if (clusterId) {
      return company.clusterId === clusterId;
    }

    // Filter by cluster name if provided
    if (clusterName && company.cluster) {
      return company.cluster.name === clusterName;
    }

    return false;
  });

  // Separate and sort companies: cluster-specific companies first (sorted by name), then global companies (sorted by name)
  const sortByName = (a, b) => (a.name || '').localeCompare(b.name || '');

  // Companies that belong to the selected cluster (have a clusterId that matches)
  const clusterCompanies = filteredCompanies
    .filter(company => {
      if (clusterId) return company.clusterId === clusterId;
      if (clusterName && company.cluster) return company.cluster.name === clusterName;
      return company.clusterId; // If no filter, any company with a cluster goes here
    })
    .sort(sortByName);

  // Global companies (no cluster assigned)
  const globalCompanies = filteredCompanies
    .filter(company => !company.clusterId)
    .sort(sortByName);

  // Combined sorted list for lookup purposes
  const sortedCompanies = [...clusterCompanies, ...globalCompanies];

  // For Select value: use the value prop directly when it's an ID, or find the ID when it's a name
  const selectValue = returnValue === COMPANY_RETURN_TYPES.NAME
    ? (companies.find(c => c.name === value)?.id || '')
    : (value || '');

  // Determine if the selector should be disabled
  const isDisabled = disabled || isLoading
    || ((!clusterId && !clusterName && !warnClusterId) && required);

  // Custom render function for displaying selected value with country and/or company type chip
  const renderSelectedValue = (selected) => {
    if (!selected) return '';

    const company = companies.find(c => c.id === selected);
    if (!company) return '';

    const showCompanyTypeChip = includeGlobalCompanies && company.companyType;

    if (showCountry || showCompanyTypeChip) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <span>{company.name}</span>
          {showCountry && company.country && (
            <Chip
              label={company.country.name}
              size="small"
              sx={{
                height: '20px',
                fontSize: '11px',
                backgroundColor: '#E0F2FE',
                color: '#0369A1',
                '& .MuiChip-label': { px: 1 }
              }}
            />
          )}
          {showCompanyTypeChip && (
            <Chip
              label={company.companyType}
              size="small"
              sx={{
                height: '20px',
                fontSize: '11px',
                backgroundColor: getCompanyTypeColor(company.companyType),
                color: getCompanyTypeTextColor(company.companyType),
                '& .MuiChip-label': { px: 1 }
              }}
            />
          )}
        </Box>
      );
    }

    return company.name;
  };

  return (
    <FormControl fullWidth={fullWidth} required={required} disabled={isDisabled} {...props}>
      <InputLabel>{label}</InputLabel>
      <Select
        value={selectValue}
        onChange={handleChange}
        label={label}
        disabled={isDisabled}
        renderValue={(showCountry || includeGlobalCompanies) ? renderSelectedValue : undefined}
        endAdornment={
          isLoading ? <CircularProgress color="inherit" size={20} /> : null
        }
        MenuProps={{
          PaperProps: {
            style: {
              maxHeight: 300,
              zIndex: 1350 // Ensure dropdown appears above table
            }
          }
        }}
      >
        {sortedCompanies.length === 0 && !isLoading ? [
          <MenuItem key="no-data" disabled>
            <Typography variant="body2" color="text.secondary">
              {(clusterId || clusterName) ? 'No companies available for this Region' : 'No companies available'}
            </Typography>
          </MenuItem>
        ] : [
          ...clusterCompanies.map(company => (
            <MenuItem key={company.id} value={company.id}>
              {(showCountry || (includeGlobalCompanies && company.companyType)) ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', pointerEvents: 'none' }}>
                  <span>{company.name}</span>
                  {showCountry && company.country && (
                    <Chip
                      component="span"
                      label={company.country.name}
                      size="small"
                      sx={{
                        height: '20px',
                        fontSize: '11px',
                        backgroundColor: '#E0F2FE',
                        color: '#0369A1',
                        '& .MuiChip-label': { px: 1 }
                      }}
                    />
                  )}
                  {includeGlobalCompanies && company.companyType && (
                    <Chip
                      component="span"
                      label={company.companyType}
                      size="small"
                      sx={{
                        height: '20px',
                        fontSize: '11px',
                        backgroundColor: getCompanyTypeColor(company.companyType),
                        color: getCompanyTypeTextColor(company.companyType),
                        '& .MuiChip-label': { px: 1 }
                      }}
                    />
                  )}
                </Box>
              ) : (
                company.name
              )}
            </MenuItem>
          )),
          ...(clusterCompanies.length > 0 && globalCompanies.length > 0 ? [
            <Divider key="divider" sx={{ my: 1 }} />
          ] : []),
          ...globalCompanies.map(company => (
            <MenuItem key={company.id} value={company.id}>
              {(showCountry || (includeGlobalCompanies && company.companyType)) ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', pointerEvents: 'none' }}>
                  <span>{company.name}</span>
                  {showCountry && company.country && (
                    <Chip
                      component="span"
                      label={company.country.name}
                      size="small"
                      sx={{
                        height: '20px',
                        fontSize: '11px',
                        backgroundColor: '#E0F2FE',
                        color: '#0369A1',
                        '& .MuiChip-label': { px: 1 }
                      }}
                    />
                  )}
                  {includeGlobalCompanies && company.companyType && (
                    <Chip
                      component="span"
                      label={company.companyType}
                      size="small"
                      sx={{
                        height: '20px',
                        fontSize: '11px',
                        backgroundColor: getCompanyTypeColor(company.companyType),
                        color: getCompanyTypeTextColor(company.companyType),
                        '& .MuiChip-label': { px: 1 }
                      }}
                    />
                  )}
                </Box>
              ) : (
                company.name
              )}
            </MenuItem>
          ))
        ]}
      </Select>
      {/* ADR-142 §8 — ajar mode: a cross-cluster company is allowed, but the
          operator is told. Reports and syncs must NOT treat this as an error. */}
      {warnClusterId && (() => {
        const selectedCompany = companies.find((company) => company.id === selectValue);
        return selectedCompany?.clusterId && selectedCompany.clusterId !== warnClusterId ? (
          <Typography sx={{ mt: 0.5, fontSize: '12px', color: '#b45309' }}>
            This company belongs to a different Region than the individual — allowed, but check it is intended.
          </Typography>
        ) : null;
      })()}
    </FormControl>
  );
};

export default CompanySelector; 